import { gql, TypedDocumentNode } from "@apollo/client";

export type UniversityEmailPreview = {
  subject: string;
  html: string;
};

export type UniversityStudentsEligibleForGraduationEmailPreviewQueryResponse = {
  universityStudentsEligibleForGraduationEmailPreview: UniversityEmailPreview;
};

export type UniversityStudentsEligibleForGraduationEmailPreviewQueryVariables = {
  universityId: string;
  personIds: string[];
  graduationDate: string;
};

export const UNIVERSITY_STUDENTS_ELIGIBLE_FOR_GRADUATION_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityStudentsEligibleForGraduationEmailPreviewQueryResponse,
  UniversityStudentsEligibleForGraduationEmailPreviewQueryVariables
> = gql`
  query UniversityStudentsEligibleForGraduationEmailPreview(
    $universityId: ID!
    $personIds: [ID!]!
    $graduationDate: ISO8601Date!
  ) {
    universityStudentsEligibleForGraduationEmailPreview(
      universityId: $universityId
      personIds: $personIds
      graduationDate: $graduationDate
    ) {
      subject
      html
    }
  }
`;

export type UniversityGraduationCongratulationsEmailPreviewQueryResponse = {
  universityGraduationCongratulationsEmailPreview: UniversityEmailPreview;
};

export type UniversityGraduationCongratulationsEmailPreviewQueryVariables = {
  graduationRequestId: string;
};

export const UNIVERSITY_GRADUATION_CONGRATULATIONS_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityGraduationCongratulationsEmailPreviewQueryResponse,
  UniversityGraduationCongratulationsEmailPreviewQueryVariables
> = gql`
  query UniversityGraduationCongratulationsEmailPreview($graduationRequestId: ID!) {
    universityGraduationCongratulationsEmailPreview(graduationRequestId: $graduationRequestId) {
      subject
      html
    }
  }
`;

export type UniversityGraduationsFinalizedEmailPreviewQueryResponse = {
  universityGraduationsFinalizedEmailPreview: UniversityEmailPreview;
};

export type UniversityGraduationsFinalizedEmailPreviewQueryVariables = {
  graduationRequestIds: string[];
  forUniversityStaff?: boolean;
};

export const UNIVERSITY_GRADUATIONS_FINALIZED_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityGraduationsFinalizedEmailPreviewQueryResponse,
  UniversityGraduationsFinalizedEmailPreviewQueryVariables
> = gql`
  query UniversityGraduationsFinalizedEmailPreview($graduationRequestIds: [ID!]!, $forUniversityStaff: Boolean) {
    universityGraduationsFinalizedEmailPreview(
      graduationRequestIds: $graduationRequestIds
      forUniversityStaff: $forUniversityStaff
    ) {
      subject
      html
    }
  }
`;

export type UniversityGraduationsRemovedEmailPreviewQueryResponse = {
  universityGraduationsRemovedEmailPreview: UniversityEmailPreview;
};

export type UniversityGraduationsRemovedEmailPreviewQueryVariables = {
  graduationRequestIds: string[];
  forUniversityStaff?: boolean;
  rejectionReason?: string | null;
};

export const UNIVERSITY_GRADUATIONS_REMOVED_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityGraduationsRemovedEmailPreviewQueryResponse,
  UniversityGraduationsRemovedEmailPreviewQueryVariables
> = gql`
  query UniversityGraduationsRemovedEmailPreview(
    $graduationRequestIds: [ID!]!
    $forUniversityStaff: Boolean
    $rejectionReason: String
  ) {
    universityGraduationsRemovedEmailPreview(
      graduationRequestIds: $graduationRequestIds
      forUniversityStaff: $forUniversityStaff
      rejectionReason: $rejectionReason
    ) {
      subject
      html
    }
  }
`;

export type UniversityRosterSubmittedEmailPreviewQueryResponse = {
  universityRosterSubmittedEmailPreview: UniversityEmailPreview;
};

export type UniversityRosterSubmittedEmailPreviewQueryVariables = {
  universityId: string;
  studentCount: number;
};

export const UNIVERSITY_ROSTER_SUBMITTED_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityRosterSubmittedEmailPreviewQueryResponse,
  UniversityRosterSubmittedEmailPreviewQueryVariables
> = gql`
  query UniversityRosterSubmittedEmailPreview($universityId: ID!, $studentCount: Int!) {
    universityRosterSubmittedEmailPreview(universityId: $universityId, studentCount: $studentCount) {
      subject
      html
    }
  }
`;

export type UniversityRosterFinalizedEmailPreviewQueryResponse = {
  universityRosterFinalizedEmailPreview: UniversityEmailPreview;
};

export type UniversityRosterFinalizedEmailPreviewQueryVariables = {
  rosterId: string;
  forUniversityStaff?: boolean;
};

export const UNIVERSITY_ROSTER_FINALIZED_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityRosterFinalizedEmailPreviewQueryResponse,
  UniversityRosterFinalizedEmailPreviewQueryVariables
> = gql`
  query UniversityRosterFinalizedEmailPreview($rosterId: ID!, $forUniversityStaff: Boolean) {
    universityRosterFinalizedEmailPreview(rosterId: $rosterId, forUniversityStaff: $forUniversityStaff) {
      subject
      html
    }
  }
`;

export type UniversityBulkEventRegistrationsEmailPreviewQueryResponse = {
  universityBulkEventRegistrationsEmailPreview: UniversityEmailPreview;
};

export type UniversityBulkEventRegistrationsEmailPreviewQueryVariables = {
  eventId: string;
  universityId: string;
  registeredCount: number;
  forUniversityStaff?: boolean;
};

export const UNIVERSITY_BULK_EVENT_REGISTRATIONS_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  UniversityBulkEventRegistrationsEmailPreviewQueryResponse,
  UniversityBulkEventRegistrationsEmailPreviewQueryVariables
> = gql`
  query UniversityBulkEventRegistrationsEmailPreview(
    $eventId: ID!
    $universityId: ID!
    $registeredCount: Int!
    $forUniversityStaff: Boolean
  ) {
    universityBulkEventRegistrationsEmailPreview(
      eventId: $eventId
      universityId: $universityId
      registeredCount: $registeredCount
      forUniversityStaff: $forUniversityStaff
    ) {
      subject
      html
    }
  }
`;
